import React, { useState, useCallback, useEffect } from 'react'
import { useEmblaCarousel } from 'embla-carousel-react'
import styled from 'styled-components'
import { useMediaQuery } from 'react-responsive'
import Box from '@components/Box'
import { SH2 } from '@components/Typography'
import ArrowLeft from '@icons/ArrowLeft.inline.svg'
import { xl } from '@styles/variables/breakpoints'
import { IButton } from '@components/Button/Button'
import Card, { ICardHeight } from './Card'
import { TCardData } from './data'

export interface IBenefits {
  heading: string
  cards: TCardData[]
  additionalButtons?: {
    [key: number]: IButton
  }
  color?: string
  cardHeight?: ICardHeight
}

interface INavButton {
  hoverColor: string
}

const NavButton = styled.button<INavButton>`
  width: 52px;
  height: 52px;
  background-color: ${({ theme }) => theme.colors.lightGray};
  border-radius: 8px;
  padding: 10px;
  border: none;
  outline: none;
  transition: 0.2s;
  cursor: pointer;
  svg path {
    transition: 0.2s;
  }

  svg path {
    fill: ${({ theme }) => theme.colors.graphite};
  }

  &:hover:not(:disabled) {
    background-color: ${({ hoverColor }) => hoverColor};

    svg path {
      fill: white;
    }
  }

  &:not(:last-child) {
    margin-right: 4px;
  }

  &:disabled {
    cursor: default;
    background: ${({ theme }) => theme.colors.lightGray};

    svg path {
      fill: ${({ theme }) => theme.colors.middleGray};
    }
  }
`

const ArrowRight = styled(ArrowLeft)`
  transform: rotate(180deg);
`

interface IPropsSDot {
  active: boolean
  color: string
}

const SDot = styled.button<IPropsSDot>`
  display: block;
  border: none;
  background: #ccc;
  width: 6px;
  height: 6px;
  padding: 0;
  flex-shrink: 0;
  flex-grow: 0;
  margin: 0 2px;
  border-radius: 100%;
  ${({ active, color }) => (active ? `background: ${color}` : '')}
`

interface IDotButton {
  selected: boolean
  onClick: () => void
  color: string
}
export const DotButton: React.FC<IDotButton> = ({ selected, onClick, color }) => (
  <SDot active={selected} type="button" onClick={onClick} color={color} />
)

const Benefits: React.FC<IBenefits> = ({ heading, cards, color = 'green', cardHeight, additionalButtons = {} }) => {
  const isLg = useMediaQuery({ minWidth: xl })
  const isMd = useMediaQuery({ maxWidth: xl - 1 })

  const sliderSettings = {
    loop: false,
    slidesToScroll: isLg ? 3 : 1,
    containScroll: ' ',
    align: 'start',
    speed: 20,
  }
  const [Slider, slider] = useEmblaCarousel(sliderSettings)
  const [prevDisabled, setPrevDisabled] = useState(false)
  const [nextDisabled, setNextDisabled] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState<number>(0)

  const scrollPrev = useCallback(() => slider && slider.scrollPrev(), [slider])
  const scrollNext = useCallback(() => slider && slider.scrollNext(), [slider])
  const scrollTo = useCallback((index: number) => slider && slider.scrollTo(index), [slider])

  const onSelect = useCallback(() => {
    if (!slider) return
    setSelectedIndex(slider.selectedScrollSnap())
    setPrevDisabled(!slider.canScrollPrev())
    setNextDisabled(!slider.canScrollNext())
  }, [slider])

  useEffect(() => {
    if (!slider) return
    slider.on('select', onSelect)
    onSelect()
  }, [slider, onSelect])

  useEffect(() => slider && slider.reInit(sliderSettings), [slider, isLg])

  return (
    <>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={{ _: '20px', xl: '30px' }}>
        <SH2>{heading}</SH2>
        {!isMd && (
          <Box display="flex">
            <NavButton onClick={scrollPrev} disabled={prevDisabled} hoverColor={color}>
              <ArrowLeft />
            </NavButton>
            <NavButton onClick={scrollNext} disabled={nextDisabled} hoverColor={color}>
              <ArrowRight />
            </NavButton>
          </Box>
        )}
      </Box>
      <Slider className="slider">
        <Box display="flex" ml="-20px">
          {cards.map((card, i) => {
            const addButton = additionalButtons[i] ? additionalButtons[i] : undefined
            return (
              <Card
                key={card.heading}
                button={addButton}
                {...card}
                number={i + 1}
                color={color}
                cardHeight={cardHeight}
              />
            )
          })}
        </Box>
      </Slider>
      {isMd && (
        <Box display="flex" mt="20px" justifyContent="center">
          {cards.map((_, index) => (
            <DotButton key={index} selected={index === selectedIndex} color={color} onClick={() => scrollTo(index)} />
          ))}
        </Box>
      )}
    </>
  )
}

export default Benefits
